import Validatable from "../../../common/mixins/validatable"
import Certificate from "./certificate";
import DimensionHandler from "../../../common/mixins/dimensionHandler"
import {CertificateSize} from "../enums/certificateSize";
import {CertificateOrientation} from "../enums/certificateOrientation";
import {CertificateFormat} from "../enums/certificateFormat";
import CertificateElementable from "../certificateElementable";
import BaseCertificateElement from "./baseCertificateElement";
import PageCertificateElement from "./pageCertificateElement";
import {HorizontalAlignment, VerticalAlignment} from "~/enums/certificates/alignment";
import Guid from "~/helpers/guid";
import CertificateFoundationLayer from "./certificateFoundationLayer";

export default class CertificateElement extends CertificateElementable(Validatable(BaseCertificateElement)) {
  x: number;
  y: number;
  //Foundation layer elements parent can be an adaptive layout section otherwise the parent will be a page
  //TODO: fix this typing
  parent: PageCertificateElement

  //Foundation Layer Elements
  _is_foundation_layer_element: boolean = false
  source_document_id: string;
  foundation_layer_client_id: string;
  override_opacity: number;
  //foundation layer elements coords relative its original parent page (should be the same on certificate its being rendered on)
  relative_x: number;
  relative_y: number;

  //Top left hand corner coordinates for parent page
  parentOriginCoords(isCardBack: boolean = false, renderMode: "edit" | "download" = "edit"): {x: number, y: number}{
    let pageStartX, pageStartY
    //For adaptive layout mode elements are always rendered to relative to their current page rather than relative to
    // the certificate as a whole
    if (this.certificate.isDynamicLayout){
      pageStartX = 0;
      pageStartY = 0;
    }else{
      pageStartX = renderMode === "edit" ? this.parent.pageBounds[0].x : this.parent.downloadPageBounds[0].x;
      pageStartY = renderMode === "edit" ? this.parent.pageBounds[0].y : this.parent.downloadPageBounds[0].y;
      if(renderMode === "edit"){
        if (isCardBack && this.certificate.size === CertificateSize.Card && this.certificate.orientation === CertificateOrientation.Portrait) {
          pageStartX += this.parent.dimensions.width + Certificate.pageMargin;
          if (this.certificate.format === CertificateFormat.Card) {
            pageStartX += Certificate.pageMargin;
          }
        }
        if (isCardBack && this.certificate.size === CertificateSize.Card && this.certificate.orientation === CertificateOrientation.Landscape) {
          pageStartY += this.parent.dimensions.height + Certificate.pageMargin;
        }
      }
      else{
        if (isCardBack){
          if (this.certificate.format === CertificateFormat.Card){
            pageStartY +=  this.parent.dimensions.height
          }
          if (this.certificate.format === CertificateFormat.FoldOver ){
            if (this.certificate.orientation === CertificateOrientation.Portrait){
              pageStartX +=  this.parent.dimensions.width
            }else{
              pageStartY += this.parent.dimensions.height
            }
          }
        }
      }

    }
    return {x: pageStartX, y: pageStartY}
  }

  //Gets X value relative to parent page
  relativeX(isCardBack: boolean = false): number {
    if (this._is_foundation_layer_element) {
      return this.relative_x
    } else {
      return this.x - this.parentOriginCoords(this.isCardBack).x;
    }
  }

  //Gets Y value relative to parent page
  relativeY(isCardBack: boolean = false): number {
    if (this._is_foundation_layer_element) {
      return this.relative_y
    } else {
      return this.y - this.parentOriginCoords(this.isCardBack).y;
    }
  }

  get horizontal_alignment(): HorizontalAlignment {
    const dimensions = this.parent.dimensions;
    const scaledWidthOfSelf = (this.scale_x * this.width);
    const certificateMargin = this.certificate.margin * DimensionHandler.defaultPixelsPer;
    const baseX = this.parent.pageBounds[0].x;

    switch (this.x) {
      case (baseX + certificateMargin):
        return HorizontalAlignment.Left;
      case baseX + (Math.round((dimensions.width - scaledWidthOfSelf) / 2)):
        return HorizontalAlignment.Middle;
      case baseX + (dimensions.width - scaledWidthOfSelf - certificateMargin):
        return HorizontalAlignment.Right;
    }
  }

  set horizontal_alignment(alignment: HorizontalAlignment) {
    const dimensions = this.parent.dimensions;
    const scaledWidthOfSelf = (this.scale_x * this.width);
    const certificateMargin = this.certificate.margin * DimensionHandler.defaultPixelsPer;
    const baseX = this.parent.pageBounds[0].x;

    switch (alignment) {
      case HorizontalAlignment.Left:
        this.x = baseX + certificateMargin;
        break;
      case HorizontalAlignment.Middle:
        this.x = baseX + Math.round((dimensions.width - scaledWidthOfSelf) / 2);
        break;
      case HorizontalAlignment.Right:
        this.x = baseX + dimensions.width - scaledWidthOfSelf - certificateMargin;
        break;
    }
  }

  get vertical_alignment(): VerticalAlignment {
    const dimensions = this.parent.dimensions;
    const scaledHeightOfSelf = (this.scale_y * this.height);
    const baseY = this.parent.pageBounds[0].y;
    const certificateMargin = this.certificate.margin * DimensionHandler.defaultPixelsPer;
    switch (this.y) {
      case (baseY + certificateMargin):
        return VerticalAlignment.Top;
      case(baseY + Math.round((dimensions.height - scaledHeightOfSelf) / 2)):
        return VerticalAlignment.Middle;
      case (baseY + (dimensions.height - scaledHeightOfSelf - certificateMargin)):
        return VerticalAlignment.Bottom
    }
  }

  set vertical_alignment(alignment: VerticalAlignment) {
    const dimensions = this.parent.dimensions;
    const scaledHeightOfSelf = (this.scale_y * this.height);
    const certificateMargin =   this.certificate.margin * DimensionHandler.defaultPixelsPer;
    const baseY = this.parent.pageBounds[0].y;
    switch (alignment) {
      case VerticalAlignment.Top:
        this.y = baseY + certificateMargin;
        break;
      case VerticalAlignment.Middle:
        this.y = baseY + Math.round((dimensions.height - scaledHeightOfSelf) / 2);
        break;
      case VerticalAlignment.Bottom:
        this.y = baseY + (dimensions.height - scaledHeightOfSelf - certificateMargin);
        break;
    }
  }

  get renderY(): number {
    if (this.certificate.is_in_download_mode) {
      return this.downloadRenderY
    } else if (this._is_foundation_layer_element) {
      return this.relative_y + this.parentOriginCoords().y
    } else {
      return this.y
    }
  }

  get renderX(): number {
    if (this.certificate.is_in_download_mode) {
      return this.downloadRenderX
    } else if (this._is_foundation_layer_element) {
      return this.relative_x + this.parentOriginCoords().x
    } else {
      return this.x
    }
  }

  get downloadRenderX(){
    return this.relativeX() + this.parentOriginCoords(this.isCardBack, "download").x
  }

  get downloadRenderY(){
    return this.relativeY() + this.parentOriginCoords(this.isCardBack, "download").y
  }

  get isCardBack(): boolean{
    if (this.certificate.size === CertificateSize.Card){
      if(this.certificate.orientation === CertificateOrientation.Portrait){
        return this.x >= (this.parent.pageBounds[0].x + this.parent.dimensions.width)
      }else{
        return this.y >= (this.parent.pageBounds[0].y + this.parent.dimensions.height)
      }
    }else{
      return false
    }
  }

  get certificateId(): string {
    //Check if this element is a part of a foundation layer
    if (!!this.source_document_id){
      return this.source_document_id
    }else{
      //This is a temporary fix - Return unpublished certificate id for published certificates
      return this.certificate.isPublishedCertificate ?  this.certificate.certificate_id : this.certificate.id;
    }

  }

  asJsonForFoundationLayerElementCreation(foundationLayer: CertificateFoundationLayer){
    const baseElementJson = this.asJson();
    baseElementJson.client_id = Guid.create();
    baseElementJson.source_document_id = this.certificateId;
    baseElementJson.override_opacity = foundationLayer.opacity;
    baseElementJson.foundation_layer_client_id = foundationLayer.client_id
    baseElementJson.is_foundation_layer_element = true
    baseElementJson.relative_x = this.relativeX()
    baseElementJson.relative_y = this.relativeY()
    return baseElementJson
  }

}